import { useEffect, useState } from 'react';

// Hooks
import { useAppSelector } from '../../../../redux/hook';

// Utils
import useDateTimeFormat from '../../../../hooks/useDateTimeFormat';
import { linkTo } from '../../../../utils/linkTo';

// Types
import { ReaderReport } from '../../../../types/ReaderReport';
import { ScanSessionStatus } from '../../../../types/ScanSessionStatus';
import { ProcessStatus } from '../../../../types/ProcessStatus';
import { usePageTitle } from '../../../../hooks/usePageTitle';

// Services
import AuthService from '../../../../services/auth.service';
import { AppPermissions } from '../../../../constants/Permissions';

// Components
import { OverviewCard, OverviewGrid, GridField } from '../../../common/OverviewCard/OverviewCard';

interface ReportSummaryProps {
  report: ReaderReport | null;
  totalCount?: number;
  source?: string;
}

const val = <T,>(v: T | undefined | null): T | string => (v == null || v === '' ? '-' : v);

export const ReportSummary = ({ report, totalCount, source }: ReportSummaryProps) => {
  const { toStandardDate, withTimeMode } = useDateTimeFormat();
  // Only the scanSessions flow produces an actual scan (with a Scan Id and a scanning user) —
  // every other entry point (e.g. the reader report list) is viewing a reader report, where
  // those fields don't apply.
  const isReaderReport = source !== 'scanSessions';
  const title = isReaderReport ? 'Reader Report' : 'Scan Report';
  const scanSessionState = useAppSelector((x) => x.scanSession);
  const readerState = useAppSelector((state) => state.readers);
  const readerReportState = useAppSelector((state) => state.readerReports);

  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const canManageReport = AuthService.hasPermission(AppPermissions.ReaderReport.Manage);
  const isSystemAdmin = AuthService.hasPermission(AppPermissions.System.Admin);

  const scanSession: any = report?.scanSession;
  const userTitle = report?.user ? `${report.user.fullName} (${report.user.email})` : undefined;

  usePageTitle(userTitle, { replaceId: true });

  useEffect(() => {
    setIsProcessing(scanSessionState.processStatus === ProcessStatus.Pending);
  }, [scanSessionState.processStatus]);

  if (!report) return null;

  const errorCount = scanSession?.errorSightingCount ?? 0;
  const unknownCount = scanSession?.unknownSightingCount ?? 0;
  const failedTotal = scanSession?.totalFailedCount ?? errorCount + unknownCount;

  // ---- Base fields (always shown) --------------------------------------------
  const baseFields: GridField[] = [
    { label: 'Reader Name', value: val(readerState?.reader?.name) },
    { label: withTimeMode('Time of Report'), value: val(toStandardDate(report.reportedDateTime)) },
    { label: withTimeMode('Last Seen'), value: val(toStandardDate(report.lastSeenDateTime)) },
    ...(canManageReport
      ? [
          { label: 'Total Sightings', value: val(readerReportState.readerReport?.sightingCount) },
          { label: 'Processed Sightings', value: val(readerReportState.readerReport?.processedCount) },
        ]
      : []),
    { label: 'Report Status', value: val(readerReportState.readerReport?.statusDescr) },
    ...(isReaderReport ? [] : [{ label: 'Scan Id', value: val(report.id) }]),
    { label: 'IMEI', value: report.imei ? linkTo('/readers', report.imei, report.imei) : val(report.imei) },
    { label: 'Station', value: val(report.stationIataCode) },
    ...(isReaderReport ? [] : [{ label: 'User', value: val(report.user?.fullName) }]),
  ];

  // ---- Scan session fields (only if a session exists) ------------------------
  const sessionFields: GridField[] = scanSession
    ? [
        { label: withTimeMode('Started'), value: val(toStandardDate(scanSession.startDateTime)) },
        { label: withTimeMode('Ended'), value: val(toStandardDate(scanSession.endDateTime)) },
        { label: 'Duration', value: val(scanSession.duration) },
        { label: 'Session Status', value: val(ScanSessionStatus[Number(scanSession.status)]) },
        {
          label: 'Process Status',
          value: ProcessStatus[Number(scanSession.processStatus)],
          valueClassName:
            scanSession.processStatus === ProcessStatus.Error
              ? 'overview-value--bad'
              : scanSession.processStatus === ProcessStatus.Processed
                ? 'overview-value--ok'
                : undefined,
        },
        ...(canManageReport
          ? [
              { label: 'Scanned', value: val(scanSession.totalScanned) },
              { label: 'Received', value: val(scanSession.totalReceived) },
              { label: 'Failed', value: val(failedTotal) },
            ]
          : []),
        { label: 'ULDs Scanned', value: val(!canManageReport ? totalCount : scanSession.uniqueDevices) },
      ]
    : [];

  // ---- Chunk into rows of 4 --------------------------------------------------
  const allFields = [...baseFields, ...sessionFields];
  const rows: GridField[][] = [];
  for (let i = 0; i < allFields.length; i += 4) {
    rows.push(allFields.slice(i, i + 4));
  }

  return (
    <OverviewCard
      title={title}
      headerRight={
        isProcessing ? (
          <span className='overview-value--bad' style={{ animation: 'overview-blink 1s ease-in-out infinite' }}>
            Processing...
          </span>
        ) : undefined
      }
    >
      <OverviewGrid columns={4} rows={rows} />

      {isSystemAdmin && scanSession && failedTotal > 0 && (
        <div className='overview-error-panel'>
          <div className='overview-error-panel__title'>Error Details</div>
          <div className='overview-error-panel__body'>
            <div>Device unknown: {unknownCount}</div>
            <div>Device not Unilode account: {errorCount}</div>
          </div>
        </div>
      )}
    </OverviewCard>
  );
};
